import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import OutletFilterSelect from "components/forms/outlet-filter-select"
import useSalesDateSummaryReportRepositories from "repositories/sales-date-summary-report"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date()
  if (Number.isNaN(date.getTime())) return ""

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateFrom = searchParams.get("date_from") || toInputDate(new Date(Date.now() - 86400000))
  const dateTo = searchParams.get("date_to") || toInputDate(new Date(Date.now() - 86400000))
  const outletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)

  const { data: reportData, isLoading } = useSalesDateSummaryReportRepositories({
    date_from: dateFrom,
    date_to: dateTo,
    outlet_id: resolvedOutletCsv || undefined,
    enabled: true,
  })

  const rows = useMemo(() => reportData?.rows ?? [], [reportData])
  const summary = useMemo(() => reportData?.summary ?? {}, [reportData])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      toast.error("Periode wajib diisi sebelum export")
      return
    }

    const query = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    })
    if (resolvedOutletCsv) query.set("outlet_id", resolvedOutletCsv)

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/sales-date-summary/export?${query.toString()}`,
      "_blank"
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Laporan Penjualan Date Summary</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Ringkasan penjualan per tanggal untuk cash & transfer, hutang, retur, discount, service charge, dan pembulatan.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="min-w-[220px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeSearchParams({ outlet_id: v })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              title="Outlet"
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Dari
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateFrom}
              onChange={(event) => mergeSearchParams({ date_from: event.target.value })}
            />
          </div>

          <div className="min-w-[160px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Tanggal Sampai
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
              value={dateTo}
              onChange={(event) => mergeSearchParams({ date_to: event.target.value })}
            />
          </div>

          <button
            type="button"
            onClick={handleExport}
            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
          >
            Ekspor Excel
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4 xl:grid-cols-6">
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Total Hari</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.total_days)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Cash & Transfer</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.cash)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">TTL Penjualan</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.ttl_penjualan)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Hutang</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.hutang)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Byr Hut. Cash</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.bayar_hutang_cash)}</div>
        </div>
        <div className="p-4 bg-white border border-neutral-200 rounded-2xl">
          <div className="text-xs font-semibold tracking-wide text-neutral-500">Retur Produk</div>
          <div className="mt-2 text-2xl font-semibold text-neutral-900">{formatNumber(summary.retur_produk)}</div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="px-4 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="text-sm font-semibold text-neutral-900">{reportData?.branch_name || "SEMUA OUTLET"}</div>
          <div className="mt-1 text-xs text-neutral-500">{reportData?.period_label || "-"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">TGL FAKTUR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">CASH & TRANSFER</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">BYR HUT. CASH</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">RETUR KASIR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">RETUR PRODUK</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">CN KASIR</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">TTL PENJUALAN</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">HUTANG</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">DISC</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">S. CHARGE</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">PEMBULATAN</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data laporan...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada data penjualan untuk periode yang dipilih.
                  </td>
                </tr>
              )}

              {!isLoading && rows.map((row) => (
                <tr key={row.invoice_date} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-800">{row.invoice_date_label || "-"}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.cash)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.bayar_hutang_cash)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.retur_kasir)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.retur_produk)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.cn_kasir)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(row.ttl_penjualan)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.hutang)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.disc)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.service_charge)}</td>
                  <td className="px-4 py-3 text-right text-neutral-800">{formatNumber(row.pembulatan)}</td>
                </tr>
              ))}
            </tbody>
            {!isLoading && rows.length > 0 && (
              <tfoot className="bg-neutral-50">
                <tr className="border-t border-neutral-200">
                  <td className="px-4 py-3 font-semibold text-neutral-900">GRAND TOTAL</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.cash)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.bayar_hutang_cash)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.retur_kasir)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.retur_produk)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.cn_kasir)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.ttl_penjualan)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.hutang)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.disc)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.service_charge)}</td>
                  <td className="px-4 py-3 font-semibold text-right text-neutral-900">{formatNumber(summary.pembulatan)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  )
}
